/**
 * Product inquiry — opens in-theme modal, pushes generate_lead to dataLayer
 * (no PII), then POSTs JSON as text/plain to Google Apps Script.
 * Apps Script writes the Sheet row and fires the Klaviyo event.
 * text/plain + no-cors avoids a CORS preflight (Apps Script cannot set CORS headers).
 */
(function () {
  const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const DATALAYER_WAIT_MS = 2000;

  function uuidv4() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0;
      var v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  function getAttribution() {
    if (typeof window.fiamettaGetAttribution === 'function') {
      return window.fiamettaGetAttribution();
    }
    return { source: 'direct', medium: 'none' };
  }

  function pushGenerateLead(leadId, form) {
    return new Promise(function (resolve) {
      var settled = false;
      function done() {
        if (settled) return;
        settled = true;
        resolve();
      }

      var attr = getAttribution();
      var productValue = Number(form.dataset.productValue);
      if (!isFinite(productValue) || productValue < 0) productValue = 0;

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'generate_lead',
        lead_id: leadId,
        product_id: form.dataset.productId ? Number(form.dataset.productId) : form.dataset.productId,
        product_title: form.dataset.productTitle || '',
        product_sku: form.dataset.productSku || '',
        product_url: form.dataset.productUrl || window.location.href,
        value: productValue,
        currency: form.dataset.currency || 'USD',
        source: attr.source,
        medium: attr.medium,
        campaign: attr.campaign || '',
        eventCallback: done,
        eventTimeout: DATALAYER_WAIT_MS,
      });

      setTimeout(done, DATALAYER_WAIT_MS);
    });
  }

  function setFieldError(field, message) {
    if (!field) return;
    var wrap = field.closest('.product-inquiry__field') || field.parentElement;
    var errorEl = wrap && wrap.querySelector('[data-inquiry-error]');
    field.setAttribute('aria-invalid', message ? 'true' : 'false');
    field.classList.toggle('product-inquiry__input--invalid', Boolean(message));
    if (errorEl) {
      errorEl.textContent = message || '';
      errorEl.hidden = !message;
    }
  }

  function clearErrors(form) {
    form.querySelectorAll('[data-inquiry-error]').forEach(function (el) {
      el.textContent = '';
      el.hidden = true;
    });
    form.querySelectorAll('[aria-invalid]').forEach(function (el) {
      el.setAttribute('aria-invalid', 'false');
      el.classList.remove('product-inquiry__input--invalid');
    });
  }

  function validate(form) {
    clearErrors(form);
    var firstInvalid = null;
    var name = form.querySelector('[name="name"]');
    var email = form.querySelector('[name="email"]');
    var message = form.querySelector('[name="message"]');
    var msgRequired = form.dataset.msgRequired || 'Required';
    var msgEmail = form.dataset.msgEmail || 'Enter a valid email';

    if (name && !(name.value || '').trim()) {
      setFieldError(name, msgRequired);
      firstInvalid = name;
    }

    if (email) {
      var emailValue = (email.value || '').trim();
      if (!emailValue) {
        setFieldError(email, msgRequired);
        if (!firstInvalid) firstInvalid = email;
      } else if (!EMAIL_PATTERN.test(emailValue)) {
        setFieldError(email, msgEmail);
        if (!firstInvalid) firstInvalid = email;
      }
    }

    if (message && !(message.value || '').trim()) {
      setFieldError(message, msgRequired);
      if (!firstInvalid) firstInvalid = message;
    }

    return { ok: !firstInvalid, firstInvalid: firstInvalid };
  }

  function collectPayload(form, leadId) {
    function get(fieldName) {
      var el = form.querySelector('[name="' + fieldName + '"]');
      if (!el) return '';
      if (el.type === 'checkbox') return el.checked ? 'yes' : 'no';
      return (el.value || '').trim();
    }

    var attr = getAttribution();
    return {
      token: form.dataset.webhookSecret || '',
      form_name: 'product_inquiry',
      lead_id: leadId,
      name: get('name'),
      email: get('email'),
      message: get('message'),
      gdpr_consent: get('gdpr_consent') === 'yes' ? 'yes' : 'no',
      product_title: form.dataset.productTitle || '',
      product_handle: form.dataset.productHandle || '',
      product_url: form.dataset.productUrl || window.location.href,
      product_sku: form.dataset.productSku || '',
      product_image: form.dataset.productImage || '',
      product_id: form.dataset.productId || '',
      page_url: window.location.href,
      source: attr.source,
      medium: attr.medium,
      campaign: attr.campaign || '',
      content: attr.content || '',
      term: attr.term || '',
      gclid: attr.gclid || '',
      fbclid: attr.fbclid || '',
      landing_page: attr.landing_page || '',
      referrer: attr.referrer || '',
      submitted_at: new Date().toISOString(),
    };
  }

  function sendToWebhook(webhookUrl, payload) {
    var body = JSON.stringify(payload);
    try {
      fetch(webhookUrl, {
        method: 'POST',
        mode: 'no-cors',
        keepalive: true,
        cache: 'no-cache',
        headers: { 'Content-Type': 'text/plain;charset=utf-8' },
        body: body,
      });
    } catch (e) {
      try {
        if (navigator.sendBeacon) {
          navigator.sendBeacon(webhookUrl, new Blob([body], { type: 'text/plain' }));
        }
      } catch (e2) {}
    }
  }

  function setLoading(form, isLoading) {
    var submitBtn = form.querySelector('[data-inquiry-submit]');
    if (!submitBtn) return;
    submitBtn.disabled = isLoading;
    submitBtn.setAttribute('aria-busy', isLoading ? 'true' : 'false');
  }

  function showSuccess(form) {
    var root = form.closest('[data-inquiry-root]');
    var formWrap = root && root.querySelector('[data-inquiry-form-wrap]');
    var successEl = root && root.querySelector('[data-inquiry-success]');
    var errorEl = root && root.querySelector('[data-inquiry-submit-error]');
    var heading = root && root.querySelector('[data-inquiry-heading]');
    var intro = root && root.querySelector('[data-inquiry-intro]');
    if (root) root.classList.add('is-success');
    if (formWrap) formWrap.hidden = true;
    if (errorEl) errorEl.hidden = true;
    if (intro) intro.hidden = true;
    if (heading) {
      var successHeading = heading.getAttribute('data-success-heading');
      if (successHeading) heading.textContent = successHeading;
    }
    if (successEl) {
      successEl.hidden = false;
      successEl.focus();
    }
  }

  function showSubmitError(form) {
    setLoading(form, false);
    var root = form.closest('[data-inquiry-root]');
    var errorEl = root && root.querySelector('[data-inquiry-submit-error]');
    if (errorEl) {
      errorEl.hidden = false;
      errorEl.focus();
    }
  }

  var submittingForms = new WeakSet();

  async function onSubmit(event) {
    var form = event.target;
    if (!form || !form.matches || !form.matches('[data-inquiry-form]')) return;

    event.preventDefault();
    event.stopPropagation();

    if (submittingForms.has(form)) return;

    var validation = validate(form);
    if (!validation.ok) {
      if (validation.firstInvalid) validation.firstInvalid.focus();
      return;
    }

    var webhookUrl = (form.dataset.webhookUrl || '').trim();
    var webhookSecret = (form.dataset.webhookSecret || '').trim();
    if (!webhookUrl || !webhookSecret) {
      showSubmitError(form);
      return;
    }

    submittingForms.add(form);
    setLoading(form, true);
    var errorEl = form.closest('[data-inquiry-root]') &&
      form.closest('[data-inquiry-root]').querySelector('[data-inquiry-submit-error]');
    if (errorEl) errorEl.hidden = true;

    var leadId = uuidv4();

    try {
      await pushGenerateLead(leadId, form);
      sendToWebhook(webhookUrl, collectPayload(form, leadId));
      showSuccess(form);
    } catch (_) {
      submittingForms.delete(form);
      showSubmitError(form);
    }
  }

  document.addEventListener('submit', onSubmit, true);

  document.addEventListener('input', function (event) {
    if (!event.target.matches('[data-inquiry-form] input, [data-inquiry-form] textarea')) return;
    setFieldError(event.target, '');
  });

  document.addEventListener('change', function (event) {
    if (!event.target.matches('[data-inquiry-form] [name="gdpr_consent"]')) return;
    setFieldError(event.target, '');
  });
})();
